import { useEffect, useMemo } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'

import { addNewProfileModalSchema } from './form/validations'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Box, useMediaQuery } from '@mui/material'
import { emotionTheme } from '@styles/theme'
import { getProfileType } from '@pages/AccessProfile/helpers/getProfileType'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { PlusCircleIcon } from '@phosphor-icons/react'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { Textarea } from '@components/shared/Textarea'
import { useAccessProfile } from '@pages/AccessProfile/context'
import { useUser } from '@hooks/useUser'

import type { AddNewProfileModalProps } from './types'
import type { AddNewProfileModalFormProps } from './form/types'

import * as S from './styles'

export const AddNewProfileModal = ({ open, onClose, ...rest }: AddNewProfileModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { userPermissionsData } = useUser()
  const { profilesData, addNewProfile, isAddNewProfileLoading } = useAccessProfile()

  const isSuperAdmin = userPermissionsData?.isSuperAdmin || userPermissionsData?.isAdmin

  const methods = useForm<AddNewProfileModalFormProps>({
    defaultValues: initialFormValues,
    resolver: addNewProfileModalSchema as unknown as Resolver<AddNewProfileModalFormProps>
  })

  const profileTypeOptions = useMemo(() => {
    return (
      profilesData?.availableFilters?.profileType
        .map((profile) => ({
          label: getProfileType(profile),
          value: profile
        }))
        .filter((item) => item.value !== 'Todos' && (isSuperAdmin ? true : item.value !== 'HIERARCHICAL')) || []
    )
  }, [profilesData, userPermissionsData])

  const statusOptions = useMemo(() => {
    return (
      profilesData?.availableFilters?.status
        .map((status) => ({ label: status, value: status }))
        .filter((item) => item.value !== 'Todos') || []
    )
  }, [profilesData, userPermissionsData])

  const onError = (errors: FieldErrors<AddNewProfileModalFormProps>) => console.log({ errors })

  const onSubmit = (formData: AddNewProfileModalFormProps) => {
    addNewProfile(formData).then((isSuccess) => {
      if (isSuccess) {
        onClose(false)
        methods.reset(initialFormValues)
      }
    })
  }

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <BaseModal2
      open={open}
      title={'Adicionar Perfil'}
      width={isMobile ? 'calc(100% - 20px)' : 870}
      heightContainer={isMobile ? 'calc(100% - 20px)' : 'auto '}
      headerIcon={<PlusCircleIcon size={24} />}
      onClose={onClose}
      footer={{
        cancelButtonText: 'Cancelar',
        cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
        confirmButtonText: 'Confirmar',
        confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
        onConfirm: () => methods.handleSubmit(onSubmit, onError)(),
        loading: isAddNewProfileLoading
      }}
      {...rest}
    >
      <S.Title>Preencha todos os campos para adicionar um novo perfil.</S.Title>

      <FormProvider {...methods}>
        <S.Form>
          <RowBox columns={5}>
            <Box sx={{ gridColumn: isMobile ? 'span 1' : 'span 3' }}>
              <Input required name="profileDescription" label="Descrição do Perfil" disabled={isAddNewProfileLoading} />
            </Box>

            <Select
              required
              name="profileType"
              label="Tipo de Perfil"
              options={profileTypeOptions}
              disabled={isAddNewProfileLoading}
            />
            <Select required name="status" label="Status" options={statusOptions} disabled={isAddNewProfileLoading} />
          </RowBox>

          <RowBox>
            <Textarea
              label="Observações"
              name="observations"
              placeholder="Descreva as suas observações (opcional)"
              disabled={isAddNewProfileLoading}
            />
          </RowBox>
        </S.Form>
      </FormProvider>
    </BaseModal2>
  )
}
